import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { WorkspaceService } from '../services/workspaceService';
import type { Workspace } from '../lib/types';

/**
 * Workspace Management Component
 * Provides CRUD interface for workspace management with GraphQL integration
 */
export function WorkspaceManager() {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState<Workspace | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    settings: '',
  });

  // Load workspaces on component mount
  useEffect(() => {
    loadWorkspaces();
  }, []);

  const loadWorkspaces = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await WorkspaceService.listWorkspaces();
      setWorkspaces(data);
    } catch (err) {
      setError('Failed to load workspaces. Please check your connection.');
      console.error('Error loading workspaces:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingWorkspace(null);
    setFormData({ name: '', domain: '', settings: '' });
    setDialogOpen(true);
  };

  const handleEdit = (workspace: Workspace) => {
    setEditingWorkspace(workspace);
    setFormData({
      name: workspace.name,
      domain: workspace.domain || '',
      settings: workspace.settings || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setError(null);
      
      if (editingWorkspace) {
        // Update existing workspace
        await WorkspaceService.updateWorkspace(editingWorkspace.id, {
          name: formData.name,
          domain: formData.domain || undefined,
          settings: formData.settings || undefined,
        });
      } else {
        // Create new workspace
        await WorkspaceService.createWorkspace({
          name: formData.name,
          domain: formData.domain || undefined,
          settings: formData.settings || undefined,
        });
      }
      
      setDialogOpen(false);
      await loadWorkspaces(); // Reload the list
    } catch (err) {
      setError('Failed to save workspace. Please try again.');
      console.error('Error saving workspace:', err);
    }
  };

  const handleDelete = async (workspace: Workspace) => {
    if (!confirm(`Are you sure you want to delete "${workspace.name}"?`)) {
      return;
    }

    try {
      setError(null);
      await WorkspaceService.deleteWorkspace(workspace.id);
      await loadWorkspaces(); // Reload the list
    } catch (err) {
      setError('Failed to delete workspace. Please try again.');
      console.error('Error deleting workspace:', err);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingWorkspace(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Workspace Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreate}
        >
          Create Workspace
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          {workspaces.length === 0 ? (
            <Typography variant="body1" color="text.secondary" textAlign="center" py={4}>
              No workspaces found. Create your first workspace to get started.
            </Typography>
          ) : (
            <List>
              {workspaces.map((workspace) => (
                <ListItem key={workspace.id} divider>
                  <ListItemText
                    primary={workspace.name}
                    secondary={
                      <>
                        {workspace.domain && (
                          <>Domain: {workspace.domain}<br /></>
                        )}
                        Created: {workspace.createdAt ? new Date(workspace.createdAt).toLocaleDateString() : 'Unknown'}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton onClick={() => handleEdit(workspace)} color="primary">
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(workspace)} color="error">
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingWorkspace ? 'Edit Workspace' : 'Create New Workspace'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Workspace Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Domain (optional)"
            fullWidth
            variant="outlined"
            value={formData.domain}
            onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
            placeholder="example.com"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Settings (JSON, optional)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={formData.settings}
            onChange={(e) => setFormData({ ...formData, settings: e.target.value })}
            placeholder='{"theme": "default"}'
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name.trim()}
          >
            {editingWorkspace ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}